import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useMyOrders, ORDER_STATUS_CONFIG, type MyOrder, type OrderStatus } from '@/hooks/useMyOrders';
import { formatMoney } from '@/lib/formatMoney';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  ChevronRight,
  MapPin,
  Package,
  ShoppingBag,
  Truck,
} from 'lucide-react';

const ACTIVE_STATUSES: OrderStatus[] = ['created', 'paid', 'processing', 'ready'];
const PAST_STATUSES: OrderStatus[] = ['completed', 'cancelled'];

// ── Components ─────────────────────────────────────

function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const config = ORDER_STATUS_CONFIG[status] ?? ORDER_STATUS_CONFIG.created;
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function OrderCard({ order }: { order: MyOrder }) {
  const itemLabel = order.itemCount === 1 ? '1 produkt' : `${order.itemCount} produkter`;
  const deliveryLabel = order.deliveryType === 'delivery' ? 'Leverans' : 'Hämtas';
  const DeliveryIcon = order.deliveryType === 'delivery' ? Truck : MapPin;
  const isInactive = order.status === 'completed' || order.status === 'cancelled';

  return (
    <Link to={`/app/customer/orders/${order.id}`} className="block group">
      <Card
        className={`transition-shadow ${
          isInactive
            ? 'opacity-70'
            : 'hover:shadow-[var(--card-shadow-hover)]'
        }`}
      >
        <CardContent className="flex items-center gap-3 sm:gap-4 p-4">
          {/* Icon */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 shrink-0">
            <Package className="h-5 w-5 text-primary" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-sm sm:text-base truncate">
                {order.producerName}
              </p>
              <OrderStatusBadge status={order.status} />
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap">
              <span>{format(new Date(order.createdAt), 'd MMM yyyy', { locale: sv })}</span>
              <span className="text-border">·</span>
              <span className="inline-flex items-center gap-1">
                <DeliveryIcon className="h-3 w-3" />
                {deliveryLabel}
              </span>
              <span className="text-border">·</span>
              <span>{itemLabel}</span>
            </div>

            {order.firstItemTitle && (
              <p className="text-xs text-muted-foreground truncate">
                {order.firstItemTitle}
                {order.itemCount > 1 && ' m.fl.'}
              </p>
            )}
          </div>

          {/* Amount + arrow */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold text-sm sm:text-base whitespace-nowrap">
              {formatMoney(order.totalAmount, order.currency)}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function EmptyState({ filter }: { filter: 'active' | 'past' }) {
  if (filter === 'past') {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
            <Package className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">Inga tidigare beställningar.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
          <ShoppingBag className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="font-medium mb-1">Inga beställningar ännu</p>
        <p className="text-sm text-muted-foreground mb-4">
          När du handlar i gårdsbutiken dyker dina beställningar upp här.
        </p>
        <Button asChild variant="outline">
          <Link to="/explore">Utforska gårdar</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="border-dashed">
          <CardContent className="flex items-center gap-4 p-4">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-52" />
            </div>
            <Skeleton className="h-5 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Page ───────────────────────────────────────────

export default function MyOrdersPage() {
  const { data: orders, isLoading, error } = useMyOrders();
  const [filter, setFilter] = useState<'active' | 'past'>('active');

  const filtered = orders?.filter((o) =>
    filter === 'active'
      ? ACTIVE_STATUSES.includes(o.status)
      : PAST_STATUSES.includes(o.status),
  );

  const activeCount = orders?.filter((o) => ACTIVE_STATUSES.includes(o.status)).length ?? 0;
  const pastCount = orders?.filter((o) => PAST_STATUSES.includes(o.status)).length ?? 0;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      {/* Back */}
      <Button asChild variant="ghost" size="sm">
        <Link to="/app/customer">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Tillbaka
        </Link>
      </Button>

      {/* Header */}
      <h1 className="font-display text-xl sm:text-2xl">Mina ordrar</h1>

      {/* Tabs */}
      {!isLoading && !error && orders && orders.length > 0 && (
        <Tabs
          value={filter}
          onValueChange={(v) => setFilter(v as 'active' | 'past')}
        >
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="active" className="flex-1 sm:flex-initial">
              Aktiva{activeCount > 0 ? ` (${activeCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="past" className="flex-1 sm:flex-initial">
              Tidigare{pastCount > 0 ? ` (${pastCount})` : ''}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Content */}
      {isLoading && <LoadingSkeleton />}

      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 text-center text-destructive">
            Kunde inte hämta ordrar. Försök igen senare.
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && orders?.length === 0 && (
        <EmptyState filter="active" />
      )}

      {!isLoading && !error && filtered && filtered.length === 0 && orders && orders.length > 0 && (
        <EmptyState filter={filter} />
      )}

      {filtered && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
}
